import { NotificationsService } from '@lib/notifications';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

import { Cid, CidService, LocalStorageService } from '@project/services';
import { SummaryApiProviderService } from '@project/data-providers';
import { outcomesWithoutCid } from './consts/outcomes-without-cid';
import { INPUT_MASKS, INPUT_PLACEHOLDERS } from '@project/shared';
import { defaultMedicalRecord } from './consts/medical-record';
import { TranslateService } from '@project/translate';
import {
  IMedicalRecordCID,
  IMedicalRecordTUSS,
  INewMedicalRecord,
  IPatientMedicalRecord,
  IRequisitionWithQuestionsAnswers,
} from '@project/view-models';
import { defaultCid } from './consts/cid';
import { defaultTuss } from './consts/tuss';
import { MedicalRecord, StoredMedicalRecord } from './types';
import { Tuss } from './tuss-select/tuss-select.component';
import { MedicalRecordStateService } from './medical-record-state.service';

@Component({
  selector: 'app-session-details-shared-medical-record',
  templateUrl: './session-details-shared-medical-record.component.html',
  styleUrls: ['./session-details-shared-medical-record.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionDetailsSharedMedicalRecordComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() requisition = new BehaviorSubject<IRequisitionWithQuestionsAnswers>(null);
  @Input() isClosingRequisition? = false;

  @Output() readonly onCloseRequisition = new EventEmitter();

  private readonly destroy$ = new Subject();

  public readonly INPUT_MASKS = INPUT_MASKS;
  public readonly INPUT_PLACEHOLDERS = INPUT_PLACEHOLDERS;

  public isCidStatusLoading = false;
  public isCidLoading: boolean[] = [];
  public isTussLoading: boolean[] = [];
  public isOutcomeLoading = false;

  public medicalRecord: MedicalRecord = JSON.parse(JSON.stringify(defaultMedicalRecord));
  public cidStatusOptions: { label: string; value: string }[] = [];
  public outcomeOptions: { label: string; value: string }[] = [];
  public requisitionID: string;
  public isFormValid = true;
  public hasPrescriptionLinks: string = null;

  // The option values must be kept as strings because the lib-select component doesn't deal well with boolean.
  // To avoid changing the lib-select component (which is used across the project), we keep this workaround here.
  public prescriptionOptions: { label: string; value: string }[] = [
    { label: `${TranslateService.localize('summary-notes.prescriptions-option1')}`, value: 'false' },
    { label: `${TranslateService.localize('summary-notes.prescriptions-option2')}`, value: 'true' },
  ];

  public maxLinks = 10;

  constructor(
    private summaryApiProviderService: SummaryApiProviderService,
    private route: ActivatedRoute,
    private notificationsService: NotificationsService,
    private el: ElementRef,
    private cidService: CidService,
    private cdr: ChangeDetectorRef,
    private localStorageService: LocalStorageService,
    private medicalStateService: MedicalRecordStateService,
  ) {}

  ngOnInit(): void {
    this.loadCidOptions();
    this.loadOutcomeOptions();
    this.setupRequisitionListener();
    this.localStorageService.clear('isDoctorSaving');
  }

  ngAfterViewInit() {
    this.el.nativeElement.querySelector('textarea').focus();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onClickAddNewLink(): void {
    this.medicalRecord.links.push('');
    this.saveMedicalRecordDraftToLocalStorage();
  }

  public onClickAddCID(): void {
    this.medicalRecord.cid.push({ ...defaultCid });
    this.saveMedicalRecordDraftToLocalStorage();
  }

  public onClickAddTuss(): void {
    this.medicalRecord.tuss.push({ ...defaultTuss });
    this.saveMedicalRecordDraftToLocalStorage();
  }

  public onClickRemoveLink(linkIndex: number): void {
    this.medicalRecord.links.splice(linkIndex, 1);
    this.saveMedicalRecordDraftToLocalStorage();
  }

  public onClickRemoveCID(cidIndex: number): void {
    this.medicalRecord.cid.splice(cidIndex, 1);
    this.saveMedicalRecordDraftToLocalStorage();
  }

  public onClickRemoveTuss(tussIndex: number): void {
    this.medicalRecord.tuss.splice(tussIndex, 1);
    this.saveMedicalRecordDraftToLocalStorage();
  }

  public onChangeHasPrescription(value: string): void {
    this.hasPrescriptionLinks = value;

    const hasSetNoPrescriptionLinks = !this.hasPrescriptionLinks || this.hasPrescriptionLinks === 'false';
    if (hasSetNoPrescriptionLinks) this.medicalRecord.links = [];
    else this.medicalRecord.links = [''];

    this.saveMedicalRecordDraftToLocalStorage();
  }

  public onChangePrescriptionLinks(): void {
    this.saveMedicalRecordDraftToLocalStorage();
  }

  public onSelectCid(cidIndex: number, cid: Cid): void {
    this.medicalRecord.cid[cidIndex].id = cid.value;
    this.medicalRecord.cid[cidIndex].name = cid.name;
    this.isFormValid = true;
    this.cidService.setCid(cid);
    this.saveMedicalRecordDraftToLocalStorage();
  }

  public onSelectCidStatus(cidIndex: number, cidStatusId: string): void {
    this.medicalRecord.cid[cidIndex].statusId = cidStatusId;
    this.isFormValid = true;
    this.saveMedicalRecordDraftToLocalStorage();
  }

  public onSelectTuss(tussIndex: number, tuss: Tuss): void {
    this.medicalRecord.tuss[tussIndex].id = tuss.value;
    this.medicalRecord.tuss[tussIndex].name = tuss.name;
    this.isFormValid = true;
    this.saveMedicalRecordDraftToLocalStorage();
  }

  public onChangeAnamnesis(anamnesis: string): void {
    this.medicalRecord.anamnesis = anamnesis;
    this.isFormValid = true;
    this.saveMedicalRecordDraftToLocalStorage();
  }

  public onChangeAdditionalSummaryNotes(notes: string): void {
    this.medicalRecord.additionalSummaryNotes = notes;
    this.saveMedicalRecordDraftToLocalStorage();
  }

  public onChangeOutcome(outcomeId: string): void {
    this.medicalRecord.outcomeId = outcomeId;
    this.isFormValid = true;
    this.saveMedicalRecordDraftToLocalStorage();
  }

  public trackByIndex = (index: number) => index;

  public setCidIsLoading(cidIndex: number, isLoading: boolean): void {
    if (this.isCidLoading) {
      this.isCidLoading[cidIndex] = isLoading;
      this.cdr.markForCheck();
    }
  }

  public setTussIsLoading(tussIndex: number, isLoading: boolean): void {
    if (this.isTussLoading) {
      this.isTussLoading[tussIndex] = isLoading;
      this.cdr.markForCheck();
    }
  }

  private getFormattedMedicalRecord(): IPatientMedicalRecord {
    const { additionalSummaryNotes, anamnesis, cid, links, outcomeId, tuss } = this.medicalRecord;

    const filteredTuss = tuss.filter((item) => !!item.id);
    const filteredCid = cid.filter((item) => !!item.id);
    const medicalRecordTUSS: IMedicalRecordTUSS[] = filteredTuss.map((item) => ({ tuss_id: item.id }));
    const medicalRecordCID: IMedicalRecordCID[] = filteredCid.map((c) => ({ cid_id: c.id, cid_status_id: c.statusId }));

    return {
      is_original_patient: true,
      anamnesis,
      medical_record_cid: medicalRecordCID,
      medical_record_tuss: medicalRecordTUSS,
      out_comes_id: outcomeId,
      additional_summary_notes: additionalSummaryNotes,
      links,
    };
  }

  private removeEmptyCidAndTussFields(): void {
    this.medicalRecord.cid = this.medicalRecord.cid.filter((cid, index) => {
      return cid.id !== null || index === 0;
    });

    this.medicalRecord.tuss = this.medicalRecord.tuss.filter((tuss, index) => {
      return tuss.id !== null || index === 0;
    });
  }

  private isValidCid(cidList: MedicalRecord['cid']): boolean {
    let isValid = true;

    cidList.forEach((cid) => {
      if (cid.id === null) {
        isValid = false;
        return;
      }
    });

    return isValid;
  }

  private isValidCidStatus(cidList: MedicalRecord['cid']): boolean {
    let isValid = true;

    cidList.forEach((cid) => {
      if (cid.statusId === null) {
        isValid = false;
        return;
      }
    });

    return isValid;
  }

  private clearCidSelection(): void {
    this.cidService.clearCid();
  }

  private showMessageError(message: string) {
    this.notificationsService.error({ message });
  }

  public onClickSubmit(): void {
    this.removeEmptyCidAndTussFields();

    const isMedicalRecordValid = this.validateMedicalRecord();
    if (!isMedicalRecordValid) return this.summaryApiProviderService.setMedicalRecordFilledState(false);

    this.sendMedicalRecord({
      requisition_id: this.requisitionID,
      medical_record: [this.getFormattedMedicalRecord()],
    });

    this.clearCidSelection();
    this.summaryApiProviderService.setMedicalRecordFilledState(true);
    this.medicalStateService.clearState();
  }

  private validateMedicalRecord(): boolean {
    const outcomeId = this.medicalRecord.outcomeId;

    const medicalEncounterDidNotHappen = outcomesWithoutCid.some((outcome) => outcome.id === outcomeId);
    if (medicalEncounterDidNotHappen) return true;

    const hasNotSetPrescriptionLinks = !this.hasPrescriptionLinks || this.hasPrescriptionLinks === 'false';
    const hasNotEmptyLinks = this.medicalRecord.links.every((link) => link.trim().length > 0);
    const areLinksValid = hasNotSetPrescriptionLinks || hasNotEmptyLinks;
    if (!areLinksValid) {
      this.showMessageError(`${TranslateService.localize('summary-notes.empty-links')}`);
      return false;
    }

    const isCidValid = this.isValidCid(this.medicalRecord.cid);
    if (!isCidValid) {
      this.showMessageError(`${TranslateService.localize('sessions-details.missing-cid')}`);
      return false;
    }

    const isCidStatusValid = this.isValidCidStatus(this.medicalRecord.cid);
    if (!isCidStatusValid) {
      this.showMessageError(`${TranslateService.localize('sessions-details.missing-status')}`);
      return false;
    }

    const isValidOutcome = this.medicalRecord.outcomeId !== null;
    if (!isValidOutcome) {
      this.showMessageError(`${TranslateService.localize('sessions-details.missing-outcome')}`);
      return false;
    }

    return true;
  }

  private sendMedicalRecord(medicalRecord: INewMedicalRecord | []): void {
    this.requisition.pipe(takeUntil(this.destroy$)).subscribe((requisition) => {
      this.onCloseRequisition.emit({ medicalRecord, requisition });
    });
  }

  private loadCidOptions(): void {
    this.isCidStatusLoading = true;
    this.summaryApiProviderService.getAllCIDStatus().subscribe((cids) => {
      this.cidStatusOptions = cids.map((status) => ({
        value: status.id,
        label: status.status,
      }));
      this.isCidStatusLoading = false;
      this.cdr.detectChanges();
    });
  }

  private loadOutcomeOptions(): void {
    this.isOutcomeLoading = true;
    this.summaryApiProviderService.getAllOutcomes().subscribe((outcomes) => {
      this.outcomeOptions = outcomes.map((outcome) => ({
        value: outcome.id,
        label: outcome.out_come,
      }));
      this.isOutcomeLoading = false;
      this.cdr.detectChanges();
    });
  }

  private setupRequisitionListener(): void {
    this.route.parent.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.requisitionID = params.requisitionId;
      this.restoreMedicalRecordState();
    });
  }

  private restoreMedicalRecordState(): void {
    const state = this.medicalStateService.getState();

    if (state && state.requisitionId === this.requisitionID) {
      this.medicalRecord = state.data;
      this.hasPrescriptionLinks = this.medicalRecord.links.length > 0 ? 'true' : 'false';
      this.cdr.markForCheck();
      return;
    }

    this.attemptToLoadMedicalRecordFromLocalStorage();
  }

  private attemptToLoadMedicalRecordFromLocalStorage(): void {
    const medicalRecordObject = this.getMedicalRecordDraftFromLocalStorage();

    if (!medicalRecordObject) return;

    const isSameRequisition = medicalRecordObject?.requisitionId === this.requisitionID;
    if (!isSameRequisition) return this.clearMedicalRecordDraftInLocalStorage();

    this.medicalRecord = medicalRecordObject.medicalRecord;
    this.hasPrescriptionLinks = this.medicalRecord.links.length > 0 ? 'true' : 'false';
    this.cdr.markForCheck();
  }

  private saveMedicalRecordDraftToLocalStorage(): void {
    const medicalRecordObject: StoredMedicalRecord = {
      requisitionId: this.requisitionID,
      medicalRecord: this.medicalRecord,
    };
    this.medicalStateService.saveState(this.requisitionID, this.medicalRecord);

    const medicalRecordStr = JSON.stringify(medicalRecordObject);

    this.localStorageService.store('requisition_data', medicalRecordStr);
  }

  private getMedicalRecordDraftFromLocalStorage(): StoredMedicalRecord | null {
    const medicalRecordStr = this.localStorageService.getItem('requisition_data');

    try {
      if (medicalRecordStr) {
        const medicalRecordObject = JSON.parse(medicalRecordStr);
        return medicalRecordObject as StoredMedicalRecord;
      }
    } catch (error) {
      console.error('Error parsing medical record from local storage:', error);
      return null;
    }
  }

  private clearMedicalRecordDraftInLocalStorage(): void {
    this.localStorageService.clear('requisition_data');
  }
}
